import { Link, NavLink } from 'react-router';
import { useEventListener } from 'usehooks-ts';
import useSession from '../../hooks/useSession';
import { PermissionFlags } from '../../features/admin/roles/PermissionFlags';
import { XIcon } from '../../components/shared/icons';
import { DemonLogoSizes, difficultyToImgSrc } from '../../utils/difficultyToImgSrc';
import { useApp } from '../../context/app/useApp';
import { routes } from '../../routes/route-definitions';
import { Bell, Book, Cog, DiceRoll, DoorOpenAlt, Duck, Home, Package, Reading, Search } from '@boxicons/react';

function MenuLink({ to, children, label }: { to: string; children: React.ReactNode; label: string }) {
    const app = useApp();

    return (
        <li>
            <NavLink
                to={to}
                end={to === '/'}
                onClick={() => app.set('showSideBar', false)}
                className={({ isActive }) =>
                    'relative flex items-center gap-3 px-3 py-2 leading-5 transition-colors round:rounded-lg ' +
                    (isActive ? 'bg-theme-700 font-semibold text-theme-text' : 'text-theme-text hover:bg-theme-700')
                }
            >
                {({ isActive }) => (
                    <>
                        <span
                            aria-hidden
                            className={
                                'absolute left-0 top-2.5 bottom-2.5 rounded-r-full bg-theme-primary transition-all ' +
                                (isActive ? 'w-0.5' : 'w-0')
                            }
                        />
                        <span aria-hidden className='flex size-5 shrink-0 items-center justify-center leading-none'>
                            {children}
                        </span>
                        <span className='truncate'>{label}</span>
                    </>
                )}
            </NavLink>
        </li>
    );
}

function NavSection({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <section className='my-3 bg-theme-900 p-2 round:rounded-xl'>
            <p className='px-2 pb-1 text-sm text-theme-400'>{label}</p>
            <ul className='space-y-1'>{children}</ul>
        </section>
    );
}

function UserSummary({ user }: { user: NonNullable<ReturnType<typeof useSession>['user']> }) {
    const app = useApp();

    return (
        <Link
            to={'/profile/' + user.ID}
            onClick={() => app.set('showSideBar', false)}
            className='mb-3 flex items-center gap-3 bg-theme-900 p-3 transition-colors hover:bg-theme-700 round:rounded-xl'
        >
            {user.avatar ? (
                <img
                    src={`https://cdn.gdladder.com/avatars/${user.ID}/${user.avatar}.png?size=56`}
                    width={56}
                    height={56}
                    className='rounded-full'
                    alt=''
                />
            ) : (
                <img
                    src={difficultyToImgSrc(user.Hardest?.Meta.Difficulty, DemonLogoSizes.SMALL)}
                    width={56}
                    height={56}
                    alt=''
                />
            )}
            <div className='min-w-0'>
                <p className='truncate text-lg font-bold'>{user.Name}</p>
                <p className='text-sm text-theme-400'>View profile</p>
            </div>
        </Link>
    );
}

function SubmitAction() {
    const app = useApp();

    return (
        <Link
            to={routes.submit.href()}
            onClick={() => app.set('showSideBar', false)}
            className='mb-3 block min-h-9 w-full round:rounded-lg border border-button-primary-border bg-button-primary/80 px-4 py-2 text-center text-lg text-white fast-spatial-transition hover:border-blue-300 hover:bg-button-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/25 round:active:rounded-md'
        >
            Submit
        </Link>
    );
}

function SignOutButton({ onSignOut }: { onSignOut: () => void }) {
    return (
        <li>
            <button
                type='button'
                onClick={onSignOut}
                className='flex w-full items-center gap-3 px-3 py-2 leading-5 text-theme-text transition-colors hover:bg-theme-700 hover:text-red-200 round:rounded-lg'
            >
                <span aria-hidden className='flex size-5 shrink-0 items-center justify-center leading-none'>
                    <DoorOpenAlt size='sm' />
                </span>
                <span>Sign out</span>
            </button>
        </li>
    );
}

export default function Sidebar() {
    const app = useApp();
    const show = app.showSideBar;
    const session = useSession();

    function closeSidebar() {
        app.set('showSideBar', false);
    }

    useEventListener('keydown', (e) => {
        if (show && e.key === 'Escape') {
            closeSidebar();
            e.stopPropagation();
        }
    });

    function onSignOut() {
        void session.logout();
        closeSidebar();
    }

    return (
        <>
            <div
                className={
                    'fixed inset-0 bg-black z-30 fast-effect-transition ' +
                    (show ? 'opacity-45' : 'opacity-0 pointer-events-none')
                }
                onClick={closeSidebar}
            />
            <div
                className={
                    'fixed right-0 top-0 z-40 h-full max-h-dvh w-80 max-w-[calc(100vw-1rem)] overflow-y-auto border-theme-outline bg-theme-950 shadow-2xl shadow-black/35 fast-effect-transition ' +
                    (show ? 'border-l' : 'translate-x-full')
                }
            >
                <div className='relative p-3'>
                    <div className='mb-3 flex h-10 items-center justify-between'>
                        <h1 className='text-2xl font-bold'>GDDL</h1>
                        <button
                            type='button'
                            onClick={closeSidebar}
                            aria-label='Close navigation menu'
                            className='grid size-9 place-items-center text-theme-text transition-colors hover:bg-theme-700 round:rounded-lg'
                        >
                            <XIcon size={30} />
                        </button>
                    </div>

                    {session.user && (
                        <>
                            <UserSummary user={session.user} />
                            <SubmitAction />
                        </>
                    )}

                    <NavSection label='GDDL'>
                        <MenuLink to='/' label='Home'>
                            <Home size='sm' />
                        </MenuLink>
                        <MenuLink to='/search' label='Search'>
                            <Search size='sm' />
                        </MenuLink>
                        <MenuLink to='/packs' label='Packs'>
                            <Package size='sm' />
                        </MenuLink>
                        <MenuLink to='/generators' label='Challenges'>
                            <DiceRoll size='sm' />
                        </MenuLink>
                    </NavSection>

                    {session.hasPermission(PermissionFlags.STAFF_DASHBOARD) && (
                        <NavSection label='Staff'>
                            <MenuLink to={routes.staff.href()} label='Dashboard'>
                                <Duck size='sm' />
                            </MenuLink>
                        </NavSection>
                    )}

                    <NavSection label='Resources'>
                        <MenuLink to='/references' label='References'>
                            <Book size='sm' />
                        </MenuLink>
                        <MenuLink to='/about' label='Guidelines'>
                            <Reading size='sm' />
                        </MenuLink>
                    </NavSection>

                    <NavSection label='Account'>
                        {session.user && (
                            <MenuLink to='/notifications' label='Notifications'>
                                <Bell size='sm' />
                            </MenuLink>
                        )}
                        <MenuLink to={session.user ? '/settings/profile' : '/settings/site'} label='Settings'>
                            <Cog size='sm' />
                        </MenuLink>
                        {session.user && <SignOutButton onSignOut={onSignOut} />}
                    </NavSection>
                </div>
            </div>
        </>
    );
}
